import { VideoEmbed } from './video-embed';
import { Suspense, type ReactNode } from 'react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { Button, buttonVariants } from '@worldcoo/ui/components/button';
import { isNotFound, Link, useRouteContext } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { CampaignStatistics } from './statistics-campaign';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { cn, heroImageSize } from '@worldcoo/ui/lib/utils';
import { Image } from '../../-components/image';
import { usePortalCampaignQuery } from '@/lib/data-hooks';
import { useCampaignDisplayContext, type CampaignDisplayCampaign } from './campaign-display-context';
import { CampaignNgosInline } from './campaign-ngos-inline';
import { useIsSingleNgoPortal } from './campaign-details';
import { DonationsIcon } from './assets/donations-icon';
import { CollectedIcon } from './assets/collected-icon';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import { NotTranslatedInfo } from './not-translated-info';
import { RingProgressBar } from './ring-progress-bar';
import { stripHtml } from '@worldcoo/shared/utils';

type CampaignNgo = CampaignDisplayCampaign['ngos'][number];

export function CampaignHero(props: { campaignId: string }) {
	const { t } = useLingui();
	const displayData = useCampaignDisplayContext();
	if (displayData) {
		const { campaign, aggregates, onDonate } = displayData;
		return (
			<CampaignHeroContent
				textsNotFound={campaign.textsNotFound}
				corporateTitle={campaign.texts.corporateTitle}
				resumeShort={campaign.texts.resumeShort}
				finished={campaign.finished}
				ngos={campaign.ngos}
				statistics={
					aggregates && (
						<StaticCampaignStatistics
							aggregates={aggregates}
							locale={displayData.locale}
							hideCollected={displayData.metadata.hideCollected}
						/>
					)
				}
				donateAction={
					campaign.finished ? (
						<Button size="2xl" className="hidden sm:w-fit lg:flex" disabled>
							{t`Campaign funded`}
						</Button>
					) : (
						<Button size="2xl" className="hidden sm:w-fit lg:flex" onClick={onDonate}>
							{t`Donate`}
						</Button>
					)
				}
				media={
					campaign.heroVideoUrl ? (
						<VideoEmbed
							url={campaign.heroVideoUrl}
							title={campaign.heroImageAlt}
							poster={campaign.media && { src: campaign.media.location, ...heroImageSize }}
						/>
					) : campaign.media?.location ? (
						// `Image`, not a bare `<img src={location}>`: that served the full-size original — 389 KB
						// against 17 KB on a measured campaign — and, having no `Cache-Control` in S3, expired from
						// the CDN daily instead of keeping imgproxy's year. The branch below already did this.
						<Image
							src={campaign.media.location}
							height={heroImageSize.height}
							aspectRatio={heroImageSize.width / heroImageSize.height}
							className={cn('w-full rounded-2xl object-cover', heroImageSize.className)}
							alt={campaign.heroImageAlt ?? 'Campaign hero image'}
							priority
						/>
					) : null
				}
			/>
		);
	}
	return (
		<Suspense fallback={<CampaignHeroPending />}>
			<SuspensedCampaignHero campaignId={props.campaignId} />
		</Suspense>
	);
}

/** Also part of the campaign route's `pendingComponent` skeleton; see page-pending.tsx. */
export function CampaignHeroPending() {
	return (
		<div className="flex flex-col lg:flex-row">
			<section className="flex w-full flex-col gap-4 px-5 pb-6 sm:gap-6 sm:px-6 sm:pb-10 lg:w-1/2">
				<Skeleton className="bg-secondary h-9 w-32" />

				<div className="flex flex-col gap-1">
					<Skeleton className="bg-secondary h-6 w-lg" />
					<Skeleton className="bg-secondary h-6 w-md" />
					<Skeleton className="bg-secondary h-6 w-lg" />
					<br />
					<Skeleton className="bg-secondary h-6 w-lg" />
					<Skeleton className="bg-secondary h-6 w-md" />
					<br />
					<Skeleton className="bg-secondary h-6 w-lg" />
					<Skeleton className="bg-secondary h-6 w-md" />
					<Skeleton className="bg-secondary h-6 w-sm" />
				</div>

				<Skeleton className="bg-secondary h-14 w-48" />
			</section>

			<div className="hidden w-full p-10 pb-14 lg:block lg:w-1/2">
				<Skeleton className={cn('bg-secondary h-full w-full rounded-2xl', heroImageSize.className)} />
			</div>
		</div>
	);
}

function SuspensedCampaignHero(props: { campaignId: string }) {
	const { portalId } = useRouteContext({ from: '__root__' });
	const { t } = useLingui();
	const campaign = usePortalCampaignQuery(props.campaignId);
	const isSingleNgo = useIsSingleNgoPortal(isNotFound(campaign) ? [] : campaign.data.ngos);

	if (!portalId || isNotFound(campaign)) return null;

	return (
		<CampaignHeroContent
			textsNotFound={campaign.data.textsNotFound}
			corporateTitle={campaign.data.texts.corporateTitle}
			resumeShort={campaign.data.texts.resumeShort}
			finished={campaign.data.finished}
			// On a single-NGO portal the author already is that NGO, so naming it again is
			// noise. Same rule the desktop "A campaign from:" card applies.
			ngos={isSingleNgo ? [] : campaign.data.ngos}
			statistics={<CampaignStatistics campaignId={props.campaignId} showFunded />}
			donateAction={
				campaign.data.finished || campaign.data.disabled ? (
					<Button size="2xl" className="hidden sm:w-fit lg:flex" disabled>
						{t`Campaign funded`}
					</Button>
				) : (
					<Link
						to="/$portalId/$campaignId/donate"
						params={{ portalId, campaignId: campaign.data.id }}
						search={(prev) => prev}
						className={cn(buttonVariants({ size: '2xl' }), 'hidden sm:w-fit lg:flex')}
					>
						{t`Donate`}
					</Link>
				)
			}
			media={
				campaign.data.heroVideoUrl ? (
					<VideoEmbed
						url={campaign.data.heroVideoUrl}
						title={campaign.data.heroVideoAlt}
						poster={campaign.data.media && { src: campaign.data.media.location, ...heroImageSize }}
					/>
				) : (
					<Image
						src={campaign.data.media?.location}
						height={heroImageSize.height}
						aspectRatio={heroImageSize.width / heroImageSize.height}
						className={cn('w-full rounded-2xl object-cover transition-all', heroImageSize.className)}
						alt={campaign.data.heroImageAlt ?? 'Campaign hero image'}
						priority
					/>
				)
			}
		/>
	);
}

function CampaignHeroContent(props: {
	textsNotFound: boolean;
	corporateTitle?: string;
	resumeShort?: string;
	finished: boolean;
	statistics: ReactNode;
	donateAction: ReactNode;
	media: ReactNode;
	ngos: CampaignNgo[];
}) {
	return (
		<DivWithPrimaryColorVariable className="bg-background">
			{props.textsNotFound && <NotTranslatedInfo className="mx-6 mb-6" />}

			<div className="flex flex-col lg:flex-row">
				<section className="flex w-full flex-col gap-4 px-5 pb-6 sm:gap-6 sm:px-6 sm:pb-10 lg:w-1/2">
					{props.statistics}

					{props.corporateTitle && (
						// TODO: migrate data to use text without HTML and remove this stripHtml usage
						<h1 className="text-2xl font-medium sm:text-3xl lg:text-4xl">{stripHtml(props.corporateTitle)}</h1>
					)}

					{props.resumeShort && (
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: props.resumeShort,
							}}
						/>
					)}

					{props.donateAction}

					{/* Mobile-only: replaces the "A campaign from:" card, which is desktop-only.
					    Last in the hero section so it sits directly above the carousel below, which
					    on mobile carries the hero image as its first slide. */}
					<CampaignNgosInline ngos={props.ngos} className="lg:hidden" />
				</section>

				{/* On mobile the hero image is merged into the carousel below (see CampaignImageGallery)
				    to gain vertical space, so it's only shown here from lg up. */}
				<div className="hidden w-full px-10 pb-14 lg:block lg:w-1/2">{props.media}</div>
			</div>
		</DivWithPrimaryColorVariable>
	);
}

function StaticCampaignStatistics(props: {
	aggregates: { donors: number; collected: number; funding?: number; progress?: number };
	locale: string;
	hideCollected: boolean;
}) {
	const { t } = useLingui();
	return (
		// Metrics strip: figures plus one-word labels, kept out of page translation. See the note on
		// the container in `statistics-campaign.tsx` for why the block and not just the numbers.
		<div
			translate="no"
			className={cn('bg-background flex gap-6 sm:gap-10 lg:gap-16', props.hideCollected && 'justify-center')}
		>
			<div className="flex flex-col font-medium">
				<DonationsIcon className="text-primary mb-2" />
				<div className="text-xl whitespace-nowrap xl:text-2xl">
					{formatNumber(props.locale, props.aggregates.donors, 0)}
				</div>
				<span className="text-primary text-sm font-semibold text-nowrap lg:text-base">{t`Donations`}</span>
			</div>
			{!props.hideCollected && (
				<div className="flex flex-col font-medium">
					<CollectedIcon className="text-primary mb-2" />
					<div className="text-xl whitespace-nowrap xl:text-2xl">
						{formatNumber(props.locale, props.aggregates.collected, 2)}€
					</div>
					{/* Left to wrap below `lg` so a large goal can't push the funded column off-screen. */}
					<span className="text-primary text-sm font-semibold lg:text-base lg:text-nowrap">
						{props.aggregates.funding
							? (() => {
									const goal = formatNumber(props.locale, props.aggregates.funding, 0);
									return t`Goal of ${goal}` + ' €';
								})()
							: t`Collected`}
					</span>
				</div>
			)}

			{/* Mobile-only: the sidebar progress card is hidden below `lg`. Truthy, not `!= null`: a 0
			    funding is "no goal", same rule as the label above — see `CampaignProgress` (TEC-3639). */}
			{Boolean(props.aggregates.funding) && (
				<div className="flex flex-col font-medium lg:hidden">
					<RingProgressBar
						radius={40}
						stroke={6}
						className="mb-2 size-8"
						inset={false}
						progress={Math.min(props.aggregates.progress ?? 0, 100)}
						strokeColor="var(--primary)"
						color="var(--primary)"
					/>
					<div className="text-xl whitespace-nowrap">{props.aggregates.progress ?? 0}%</div>
					<span className="text-primary text-sm font-semibold text-nowrap">{t`Funded`}</span>
				</div>
			)}
		</div>
	);
}
